/** @format */

import TooltipButton from '@atoms/TooltipButton';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import clsx from 'clsx';
import { motion } from 'motion/react';
import React, { Dispatch, SetStateAction } from 'react';
import { FormattedMessage } from 'react-intl';
import styles from './DocumentSummary.module.css';

interface DocSummProps {
  summary: Array<{ documentStatus: DocumentStatus; count: number }>;
  filter?: DocumentStatus;
  setFilter?: Dispatch<SetStateAction<DocumentStatus>>;
  hideLabels?: boolean;
  dark?: boolean;
}

// I know having these two overlapping enums is dumb but as you currently can't extend an enum in TS this seemed like the best/easiest solution
// Having them all under one enum seems weird as although they are linked they are also semantically different
export enum DocumentStatus {
  ALL = 'total',
  NONE = 'orphaned',
  CREATED = 'created',
  PROVIDED = 'provided',
  ACCEPTED = 'accepted',
  REJECTED = 'rejected',
}

export const docSummaryTypeConfig = {
  [DocumentStatus.CREATED]: {
    icon: <FontAwesomeIcon icon={['fal', 'file-plus']} />,
    label: <FormattedMessage defaultMessage="Created" description="Created date label label" />,
  },
  [DocumentStatus.PROVIDED]: {
    icon: <FontAwesomeIcon icon={['fal', 'paper-plane']} />,
    label: <FormattedMessage defaultMessage="Provided to Customer" description="Document has been sent to the customer" />,
  },
  [DocumentStatus.ACCEPTED]: {
    icon: <FontAwesomeIcon icon={['fal', 'file-signature']} />,
    label: <FormattedMessage defaultMessage="Approved" description="Signed document has been approved by the customer" />,
  },
  [DocumentStatus.NONE]: {
    icon: <FontAwesomeIcon icon={['fal', 'question-circle']} />,
    label: <FormattedMessage defaultMessage="No Status" description="Documents that have not been given a status (e.g. created, provided to customer etc.) yet" />,
  },
  [DocumentStatus.ALL]: {
    icon: <FontAwesomeIcon icon={['fal', 'file']} />,
    label: <FormattedMessage defaultMessage="Total" description="Label for the total number of documents associated with an account" />,
  },
  [DocumentStatus.REJECTED]: {
    icon: <FontAwesomeIcon icon={['fal', 'file-slash']} />,
    label: <FormattedMessage defaultMessage="Rejected" description="Not Accepted" />,
  },
};

function DocumentSummary(props: DocSummProps) {
  const containsUnknownStatus = !!Number(props.summary?.find((s) => s.documentStatus === DocumentStatus.NONE)?.count);
  const categories = containsUnknownStatus ? Object.keys(DocumentStatus) : Object.keys(DocumentStatus).filter((s) => DocumentStatus[s] !== DocumentStatus.NONE);
  return (
    <div className={clsx(styles.container)}>
      {categories.map((t) => {
        const type = DocumentStatus[t];
        const config = docSummaryTypeConfig[type];
        const icon = config?.icon;
        const isSelected = props.filter === type;

        return (
          <span
            className={clsx(styles.item, { [styles.active]: isSelected })}
            key={`summary-${t}`}
            style={{ cursor: !!props.setFilter ? 'pointer' : 'default' }}
            onClick={() => props.setFilter?.(type)}
          >
            {!!props.setFilter && icon}
            <span>
              {!props.setFilter && (props.hideLabels ? <TooltipButton tooltip={config?.label}>{icon}</TooltipButton> : icon)}
              {!props.hideLabels && <span className={styles.label}>{config?.label}</span>}
              <span className={styles.count}>{props.summary?.find((s) => s.documentStatus === DocumentStatus[t])?.count ?? 0}</span>
            </span>
            {isSelected && <motion.div className={styles.underline} layoutId={'underline'} />}
          </span>
        );
      })}
    </div>
  );
}

export default DocumentSummary;
